import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, BellOff, Check } from "lucide-react";
import {
  getUnreadCount,
  listNotifications,
  markAllRead,
  markRead,
  type NotificationList,
} from "../lib/notifications";
import { navigate } from "../lib/router";

export function NotificationBell() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Badge: nur unread-count im Hintergrund, billig
  const { data: counter } = useQuery({
    queryKey: ["notifs-count"],
    queryFn: () => getUnreadCount(),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  // Volle Liste nur laden wenn das Dropdown offen ist
  const { data, isLoading } = useQuery<NotificationList>({
    queryKey: ["notifs-list"],
    queryFn: () => listNotifications(false),
    enabled: open,
  });

  const readOne = useMutation({
    mutationFn: (id: string) => markRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifs-count"] });
      qc.invalidateQueries({ queryKey: ["notifs-list"] });
    },
  });

  const readAll = useMutation({
    mutationFn: () => markAllRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifs-count"] });
      qc.invalidateQueries({ queryKey: ["notifs-list"] });
    },
  });

  // Click-Außerhalb schließt das Dropdown
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const unread = counter?.unread_count || 0;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative size-9 grid place-items-center rounded-full text-ink-soft hover:bg-cream-soft hover:text-ink"
        aria-label={unread > 0 ? `${unread} neue Nachrichten` : "Benachrichtigungen"}
        title="Benachrichtigungen"
      >
        <Bell className="size-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold grid place-items-center">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-11 z-50 w-[min(380px,calc(100vw-2rem))] rounded-2xl bg-white shadow-2xl border border-line overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-line bg-cream-soft/40">
            <div className="text-sm font-semibold">Benachrichtigungen</div>
            {unread > 0 && (
              <button
                onClick={() => readAll.mutate()}
                disabled={readAll.isPending}
                className="text-xs text-accent-orange hover:underline inline-flex items-center gap-0.5"
              >
                <Check className="size-3" /> alle gelesen
              </button>
            )}
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {isLoading && (
              <div className="px-3 py-6 text-center text-xs text-ink-muted">Lade…</div>
            )}
            {data && data.items.length === 0 && (
              <div className="px-3 py-8 text-center">
                <BellOff className="size-6 text-ink-muted mx-auto mb-1" />
                <div className="text-xs text-ink-muted">Hier ist es noch ruhig.</div>
              </div>
            )}
            {data &&
              data.items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => {
                    if (!n.read_at) readOne.mutate(n.id);
                    if (n.url) {
                      navigate(n.url);
                      setOpen(false);
                    }
                  }}
                  className={`w-full text-left px-3 py-2.5 border-b border-line/60 last:border-0 flex items-start gap-2 hover:bg-cream-soft/40 ${
                    !n.read_at ? "bg-cream-soft/20" : ""
                  }`}
                >
                  <span className="text-lg flex-shrink-0 mt-0.5">
                    {n.icon || (n.kind === "commission" ? "💰" : n.kind === "feedback_reply" ? "💬" : "🔔")}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className={`text-sm leading-tight ${!n.read_at ? "font-semibold" : "font-medium"}`}>
                        {n.title}
                      </div>
                      {!n.read_at && (
                        <span className="flex-shrink-0 size-2 rounded-full bg-accent-orange mt-1.5" />
                      )}
                    </div>
                    {n.body && (
                      <div className="text-xs text-ink-muted mt-0.5 leading-snug line-clamp-2">
                        {n.body}
                      </div>
                    )}
                    <div className="text-[10px] text-ink-muted mt-0.5">
                      {timeAgo(n.created_at)}
                    </div>
                  </div>
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

function timeAgo(iso: string): string {
  const now = Date.now();
  const t = new Date(iso).getTime();
  const diff = Math.max(0, now - t);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "gerade eben";
  if (m < 60) return `vor ${m} Min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `vor ${h} Std`;
  const d = Math.floor(h / 24);
  if (d < 7) return `vor ${d} Tagen`;
  return new Date(iso).toLocaleDateString("de-DE");
}
